import { z } from "zod";

/**
 * A school's academic structure: academic years, classes (grade levels in order) and their
 * sections. Used by the API and the School settings screens, so both apply the same rules.
 */

// ---------------------------------------------------------------------------------------------
// Academic years
// ---------------------------------------------------------------------------------------------

export const ACADEMIC_YEAR_STATUSES = ["planned", "current", "closed"] as const;
export type AcademicYearStatus = (typeof ACADEMIC_YEAR_STATUSES)[number];

export const ACADEMIC_YEAR_STATUS_LABELS: Record<AcademicYearStatus, string> = {
  planned: "Planned",
  current: "Current",
  closed: "Closed",
};

const isoDate = (empty: string) =>
  z
    .string()
    .trim()
    .min(1, { error: empty, abort: true })
    .pipe(z.iso.date({ error: "Enter the date as YYYY-MM-DD" }));

/** "2026-27": the start year and the last two digits of the next one. */
export const academicYearNameSchema = z
  .string()
  .trim()
  .min(1, { error: "Enter the year, like 2026-27", abort: true })
  .regex(/^(\d{4})-(\d{2})$/, { error: "Write the year like 2026-27" })
  .refine(
    (value) => {
      const [start, end] = value.split("-");
      return (Number(start) + 1) % 100 === Number(end);
    },
    { error: "The second part is the next year, like 2026-27" },
  );

const yearFields = {
  name: academicYearNameSchema,
  startsOn: isoDate("Enter the first day"),
  endsOn: isoDate("Enter the last day"),
};

const endsAfterStart = (value: { startsOn?: string; endsOn?: string }) =>
  !value.startsOn || !value.endsOn || value.endsOn > value.startsOn;
const endIssue = {
  error: "The last day must be after the first day",
  path: ["endsOn"],
  when: (payload: { issues: readonly { path?: readonly PropertyKey[] }[] }) =>
    !payload.issues.some((issue) => ["startsOn", "endsOn"].includes(String(issue.path?.[0]))),
};

export const academicYearCreateSchema = z.object(yearFields).refine(endsAfterStart, endIssue);

export const academicYearUpdateSchema = z
  .object({ ...yearFields, version: z.number().int().min(1) })
  .refine(endsAfterStart, endIssue);

/** Making a year current closes the one that was current. */
export const academicYearCurrentSchema = z.object({ version: z.number().int().min(1) });

export type AcademicYearCreate = z.output<typeof academicYearCreateSchema>;
export type AcademicYearUpdate = z.output<typeof academicYearUpdateSchema>;

export interface AcademicYearDto {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
  status: AcademicYearStatus;
  version: number;
}

// ---------------------------------------------------------------------------------------------
// Classes and sections
// ---------------------------------------------------------------------------------------------

/** NEP 2020 stages, used to group classes (and later wings and report cards). */
export const CLASS_STAGES = [
  "pre_primary",
  "primary",
  "middle",
  "secondary",
  "senior_secondary",
] as const;
export type ClassStage = (typeof CLASS_STAGES)[number];

export const CLASS_STAGE_LABELS: Record<ClassStage, string> = {
  pre_primary: "Pre-primary",
  primary: "Primary (1–5)",
  middle: "Middle (6–8)",
  secondary: "Secondary (9–10)",
  senior_secondary: "Senior secondary (11–12)",
};

export const STREAMS = ["science", "commerce", "humanities"] as const;
export type Stream = (typeof STREAMS)[number];

export const STREAM_LABELS: Record<Stream, string> = {
  science: "Science",
  commerce: "Commerce",
  humanities: "Humanities",
};

export const RECORD_STATUSES = ["active", "retired"] as const;
export type RecordStatus = (typeof RECORD_STATUSES)[number];

/** "8" is shown as "Class 8"; "Nursery" or "LKG" as they are. */
export function classTitle(name: string): string {
  return /^\d{1,2}$/.test(name) ? `Class ${name}` : name;
}

/** "8-A", as on registers and ID cards. */
export function sectionLabel(className: string, sectionName: string): string {
  return `${className}-${sectionName}`;
}

/** Ready-made class lists a school can start from; existing names are skipped. */
export const CLASS_PRESETS = {
  nursery_to_12: ["Nursery", "LKG", "UKG", ...Array.from({ length: 12 }, (_, i) => `${i + 1}`)],
  lkg_to_12: ["LKG", "UKG", ...Array.from({ length: 12 }, (_, i) => `${i + 1}`)],
  one_to_12: Array.from({ length: 12 }, (_, i) => `${i + 1}`),
  one_to_10: Array.from({ length: 10 }, (_, i) => `${i + 1}`),
} as const;
export type ClassPreset = keyof typeof CLASS_PRESETS;

export const CLASS_PRESET_LABELS: Record<ClassPreset, string> = {
  nursery_to_12: "Nursery, LKG, UKG and Classes 1 to 12",
  lkg_to_12: "LKG, UKG and Classes 1 to 12",
  one_to_12: "Classes 1 to 12",
  one_to_10: "Classes 1 to 10",
};

/** The stage a class name usually belongs to; the school can change it. */
export function stageFor(name: string): ClassStage {
  const grade = Number(name);
  if (!Number.isInteger(grade)) return "pre_primary";
  if (grade <= 5) return "primary";
  if (grade <= 8) return "middle";
  if (grade <= 10) return "secondary";
  return "senior_secondary";
}

const className = z
  .string()
  .trim()
  .min(1, { error: "Enter the class name, like 8 or LKG" })
  .max(40, { error: "Class name is too long" });

export const classCreateSchema = z.object({
  name: className,
  stage: z.enum(CLASS_STAGES, { error: "Choose the stage" }),
});

export const classUpdateSchema = z.object({
  name: className,
  stage: z.enum(CLASS_STAGES, { error: "Choose the stage" }),
  status: z.enum(RECORD_STATUSES, { error: "Choose a status" }),
  version: z.number().int().min(1),
});

/** The classes in the order the school wants them shown (every class, once). */
export const classOrderSchema = z.object({
  ids: z
    .array(z.string().regex(/^cls_[0-9A-Z]{26}$/, { error: "Unknown class" }))
    .min(1)
    .max(60, { error: "Too many classes" })
    .refine((ids) => new Set(ids).size === ids.length, { error: "A class is listed twice" }),
});

export const classPresetSchema = z.object({
  preset: z.enum(Object.keys(CLASS_PRESETS) as [ClassPreset, ...ClassPreset[]], {
    error: "Choose a list of classes",
  }),
});

export type ClassCreate = z.output<typeof classCreateSchema>;
export type ClassUpdate = z.output<typeof classUpdateSchema>;

/** Optional whole number in a range: blank becomes null. */
const optionalCount = (max: number) =>
  z.preprocess(
    (value) => (value === "" || value === undefined ? null : value),
    z.coerce
      .number({ error: "Enter a number" })
      .int({ error: "Enter a whole number" })
      .min(1, { error: "Enter at least 1" })
      .max(max, { error: `At most ${max}` })
      .nullable(),
  );

// ---------------------------------------------------------------------------------------------
// Where a section sits
// ---------------------------------------------------------------------------------------------

/** Floors as schools say them: basement, ground, then 1st, 2nd … 20th. */
export const FLOOR_MIN = -1;
export const FLOOR_MAX = 20;

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th"}`;
}

export function floorLabel(floor: number): string {
  if (floor === -1) return "Basement";
  if (floor === 0) return "Ground floor";
  return `${ordinal(floor)} floor`;
}

/** Every floor a section can be on, lowest first. */
export const FLOORS: readonly number[] = Array.from(
  { length: FLOOR_MAX - FLOOR_MIN + 1 },
  (_, index) => FLOOR_MIN + index,
);

/** "Room 12 · 1st floor · Main block", or null when nothing is set. */
export function sectionPlace(section: {
  room: string | null;
  floor: number | null;
  building: string | null;
}): string | null {
  const parts = [
    section.room ? `Room ${section.room}` : null,
    section.floor === null ? null : floorLabel(section.floor),
    section.building,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** Blank becomes null; left out (an older form) keeps what's saved. */
const optionalFloor = z
  .preprocess(
    (value) => (value === "" ? null : value),
    z.coerce
      .number({ error: "Choose a floor" })
      .int({ error: "Choose a floor" })
      .min(FLOOR_MIN, { error: "Choose a floor from the list" })
      .max(FLOOR_MAX, { error: "Choose a floor from the list" })
      .nullable(),
  )
  .optional();

const optionalBuilding = z
  .string()
  .trim()
  .max(60, { error: "Building name is too long" })
  .transform((value) => (value === "" ? null : value))
  .nullable()
  .optional();

const sectionFields = {
  name: z
    .string()
    .trim()
    .toUpperCase()
    .min(1, { error: "Enter the section, like A", abort: true })
    .max(10, { error: "Section name is too long" })
    .regex(/^[A-Z0-9][A-Z0-9 -]*$/, { error: "Use letters and numbers, like A or ROSE" }),
  classTeacherId: z
    .string()
    .trim()
    .regex(/^usr_[0-9A-Z]{26}$/, { error: "Choose a teacher" })
    .nullable()
    .default(null),
  capacity: optionalCount(200),
  room: z
    .string()
    .trim()
    .max(40, { error: "Room number is too long" })
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .default(null),
  floor: optionalFloor,
  building: optionalBuilding,
  stream: z.enum(STREAMS, { error: "Choose a stream" }).nullable().default(null),
};

export const sectionCreateSchema = z.object({
  classId: z.string().regex(/^cls_[0-9A-Z]{26}$/, { error: "Choose a class" }),
  ...sectionFields,
});

export const sectionUpdateSchema = z.object({
  ...sectionFields,
  status: z.enum(RECORD_STATUSES, { error: "Choose a status" }),
  version: z.number().int().min(1),
});

// ---------------------------------------------------------------------------------------------
// Seating plans
// ---------------------------------------------------------------------------------------------

/** Biggest room a plan can describe: enough for any classroom or hall. */
export const SEATING_MAX = { rows: 15, columns: 12, seatsPerDesk: 4 } as const;

const seatingCount = (what: string, one: string, max: number) =>
  z
    .number({ error: `Enter the number of ${what}` })
    .int({ error: `Enter the number of ${what}` })
    .min(1, { error: `At least ${one}` })
    .max(max, { error: `At most ${max} ${what}` });

const inRange = (value: unknown, max: number) =>
  Number.isInteger(value) && (value as number) >= 1 && (value as number) <= max;

const gapKey = (row: number, column: number) => `${row}-${column}`;

function readGap(gap: string): { row: number; column: number } {
  const [row = 0, column = 0] = gap.split("-").map(Number);
  return { row, column };
}

/**
 * A section's seating plan: rows × columns of desks seen from the board, each desk seating
 * `seatsPerDesk` students. `gaps` are places with no desk ("row-column", counting from 1 at the
 * board's left), for aisles and pillars.
 */
export const seatingLayoutSchema = z
  .object({
    rows: seatingCount("rows", "1 row", SEATING_MAX.rows),
    columns: seatingCount("columns", "1 column", SEATING_MAX.columns),
    seatsPerDesk: seatingCount("seats a desk", "1 seat a desk", SEATING_MAX.seatsPerDesk),
    gaps: z
      .array(z.string().regex(/^\d{1,2}-\d{1,2}$/, { error: "Unknown place in the room" }))
      .max(SEATING_MAX.rows * SEATING_MAX.columns, { error: "Too many gaps" })
      .default([]),
  })
  .superRefine((layout, ctx) => {
    // Gaps are checked against a room that makes sense, or the errors would only repeat.
    if (!inRange(layout.rows, SEATING_MAX.rows) || !inRange(layout.columns, SEATING_MAX.columns)) {
      return;
    }
    const gaps = new Set(layout.gaps);
    for (const gap of gaps) {
      const { row, column } = readGap(gap);
      if (row < 1 || column < 1 || row > layout.rows || column > layout.columns) {
        ctx.addIssue({ code: "custom", path: ["gaps"], message: "A gap is outside the room" });
        return;
      }
    }
    if (gaps.size >= layout.rows * layout.columns) {
      ctx.addIssue({ code: "custom", path: ["gaps"], message: "Leave at least one desk" });
    }
  })
  .transform((layout) => ({
    ...layout,
    // One way to write the same plan, so copies compare equal.
    gaps: [...new Set(layout.gaps)].sort((a, b) => {
      const x = readGap(a);
      const y = readGap(b);
      return x.row - y.row || x.column - y.column;
    }),
  }));

export type SeatingLayout = z.output<typeof seatingLayoutSchema>;
export type SeatingLayoutInput = z.input<typeof seatingLayoutSchema>;

export function isSeatingGap(layout: SeatingLayoutInput, row: number, column: number): boolean {
  return (layout.gaps ?? []).includes(gapKey(row, column));
}

/** The plan with one place switched between desk and gap. */
export function toggleSeatingGap(
  layout: SeatingLayoutInput,
  row: number,
  column: number,
): SeatingLayoutInput {
  const key = gapKey(row, column);
  const gaps = layout.gaps ?? [];
  return {
    ...layout,
    gaps: gaps.includes(key) ? gaps.filter((gap) => gap !== key) : [...gaps, key],
  };
}

/** Desks and seats, counting only gaps inside the room. */
export function seatingTotals(layout: SeatingLayoutInput): { desks: number; seats: number } {
  const inside = new Set(
    (layout.gaps ?? []).filter((gap) => {
      const { row, column } = readGap(gap);
      return row >= 1 && column >= 1 && row <= layout.rows && column <= layout.columns;
    }),
  );
  const desks = Math.max(layout.rows * layout.columns - inside.size, 0);
  return { desks, seats: desks * layout.seatsPerDesk };
}

/**
 * Desk numbers as students would count them: row by row from the board, left to right,
 * skipping gaps. `null` where there's no desk.
 */
export function deskNumbers(layout: SeatingLayoutInput): (number | null)[][] {
  let next = 1;
  return Array.from({ length: layout.rows }, (_, r) =>
    Array.from({ length: layout.columns }, (_, c) =>
      isSeatingGap(layout, r + 1, c + 1) ? null : next++,
    ),
  );
}

const sectionIdSchema = z.string().regex(/^sec_[0-9A-Z]{26}$/, { error: "Choose a section" });

/** Save a section's plan, or remove it (null). */
export const sectionSeatingSchema = z.object({
  layout: seatingLayoutSchema.nullable(),
  version: z.number().int().min(1),
});

/** A class's default plan, which its new sections start with; null removes it. */
export const classSeatingSchema = sectionSeatingSchema;

/** The sections a plan is copied to (a section's own, or its class's default). */
export const seatingCopySchema = z.object({
  sectionIds: z
    .array(sectionIdSchema)
    .min(1, { error: "Choose at least one section" })
    .max(100, { error: "Too many sections at once" })
    .transform((ids) => [...new Set(ids)]),
});

export type SectionSeating = z.output<typeof sectionSeatingSchema>;
export type SeatingCopy = z.output<typeof seatingCopySchema>;

export type SectionCreate = z.output<typeof sectionCreateSchema>;
export type SectionCreateInput = z.input<typeof sectionCreateSchema>;
export type SectionUpdate = z.output<typeof sectionUpdateSchema>;

export interface ClassSectionDto {
  id: string;
  name: string;
  /** "8-A" */
  label: string;
  classTeacher: { id: string; displayName: string } | null;
  capacity: number | null;
  /** Room number, as the school writes it. */
  room: string | null;
  floor: number | null;
  building: string | null;
  stream: Stream | null;
  status: RecordStatus;
  /** Active students in the section now. */
  studentCount: number;
  seating: SeatingLayout | null;
  /** What the signed-in person may do with this section's seating plan. */
  can: { designSeating: boolean; copySeating: boolean };
  version: number;
}

export interface ClassDto {
  id: string;
  name: string;
  stage: ClassStage;
  position: number;
  status: RecordStatus;
  version: number;
  /** The plan new sections of this class start with. */
  defaultSeating: SeatingLayout | null;
  can: { setDefaultSeating: boolean };
  sections: ClassSectionDto[];
}

/** Someone who can be made a class teacher: an active teacher, principal or admin. */
export interface StaffOptionDto {
  id: string;
  displayName: string;
  loginId: string;
}
